import { SiteLayout } from "@/components/layout/SiteLayout";
import { SEO } from "@/components/SEO";
import { Link } from "wouter";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { RelatedPages } from "@/components/RelatedPages";
import { breadcrumbSchema, faqSchema, personSchema, medicalOrganizationSchema, webPageSchema } from "@/lib/seo";
import { withNetworkUtm } from "@/lib/network";

const SEO_TITLE = "Work with Dr. Refai at shrinkMD | Shariq Refai, MD";
const SEO_DESCRIPTION =
  "How to become a patient at shrinkMD, the telepsychiatry practice founded by Shariq Refai, MD, MBA. States open now, what's covered, and how to start care.";

// Mirrors shrinkmd.com/locations (checked 2026-09-24). Update both lists
// together when shrinkMD opens a state.
const STATES_OPEN = ["Maine", "Nebraska"];
const STATES_COMING_SOON = [
  "California",
  "Florida",
  "Georgia",
  "Hawaii",
  "Indiana",
  "New York",
  "Texas",
  "Virginia",
];
const joinList = (xs: string[]) =>
  xs.length < 2 ? xs.join("") : `${xs.slice(0, -1).join(", ")}${xs.length > 2 ? "," : ""} and ${xs[xs.length - 1]}`;

const FAQ_ITEMS = [
  {
    question: "Is shariqrefai.com the same as shrinkMD?",
    answer:
      "No. shariqrefai.com is Dr. Refai's educational and media platform. It doesn't provide clinical care and doesn't accept patients. shrinkMD is the separate telepsychiatry practice he founded and where he sees patients. They're intentionally kept separate for legal, editorial, and regulatory reasons.",
  },
  {
    question: "Which states does shrinkMD currently serve?",
    answer:
      "shrinkMD is open to patients in Maine and Nebraska. California, Florida, Georgia, Hawaii, Indiana, New York, Texas, and Virginia are coming soon, and you can join the waitlist for those states on shrinkmd.com. The current list is always on shrinkmd.com/locations.",
  },
  {
    question: "How do I become a patient at shrinkMD?",
    answer:
      "Visit shrinkmd.com and start care there. The clinical intake, appointment scheduling, and clinical care all happen on shrinkmd.com, not here.",
  },
  {
    question: "Does shrinkMD accept insurance?",
    answer:
      "No. shrinkMD is a private-pay practice and doesn't bill commercial insurance, Medicare, or Medicaid. On request it can provide a superbill you can submit to your plan for possible out-of-network reimbursement, which your plan decides. Current pricing is on shrinkmd.com.",
  },
  {
    question: "Is Dr. Refai personally seeing patients, or does shrinkMD have other psychiatrists?",
    answer:
      "Dr. Refai founded and leads shrinkMD and sees patients there. The full clinical team is listed on shrinkmd.com, and every clinician follows the same clinical standards.",
  },
  {
    question: "What if I'm in a state shrinkMD doesn't cover?",
    answer:
      "shrinkMD can only see patients in states where its clinicians are licensed. If you're outside those states, Psychology Today's psychiatrist directory, your insurance's provider directory, or the SAMHSA behavioral health treatment locator (findtreatment.gov) are reasonable places to find a psychiatrist near you.",
  },
  {
    question: "Can shrinkMD help in a psychiatric emergency or crisis?",
    answer:
      "No. shrinkMD is an outpatient telepsychiatry practice, not a crisis service. If you're in crisis or thinking about suicide, call or text 988 in the United States for the Suicide and Crisis Lifeline, call 911, or go to your nearest emergency room. Those are the right resources for an emergency.",
  },
  {
    question: "Does shrinkMD see children or adolescents?",
    answer:
      "shrinkMD's clinicians focus on adult psychiatric care. Children and adolescents typically need care from a child and adolescent psychiatrist. Ask your pediatrician for a referral or use the American Academy of Child and Adolescent Psychiatry's finder at aacap.org.",
  },
];

export default function WorkWithDrRefai() {
  const breadcrumbs = [
    { name: "Home", path: "/" },
    { name: "Work with Dr. Refai at shrinkMD", path: "/work-with-dr-refai" },
  ];

  const pageSchema = webPageSchema({
    path: "/work-with-dr-refai",
    title: SEO_TITLE,
    description: SEO_DESCRIPTION,
    breadcrumbs,
    speakableSelectors: [".care-summary"],
  });

  const faqLd = faqSchema(FAQ_ITEMS.map((f) => ({ question: f.question, answer: f.answer })));

  return (
    <SiteLayout>
      <SEO
        title={SEO_TITLE}
        description={SEO_DESCRIPTION}
        url="/work-with-dr-refai"
        image="/og/work-with-dr-refai.jpg"
        schema={[
          personSchema,
          medicalOrganizationSchema,
          pageSchema,
          breadcrumbSchema(breadcrumbs),
          faqLd,
        ]}
      />

      {/* Intro */}
      <section className="pt-24 pb-12 md:pt-32 md:pb-16 px-4 md:px-8 bg-muted/30 border-b border-border/50">
        <div className="container mx-auto max-w-3xl">
          <Breadcrumbs
            items={[
              { name: "Home", href: "/" },
              { name: "Work with Dr. Refai at shrinkMD" },
            ]}
          />
          <span className="text-accent text-xs font-bold tracking-[0.25em] uppercase">
            Work with Dr. Refai
          </span>
          <h1 className="mt-4 text-4xl md:text-5xl font-serif text-primary leading-tight tracking-tight">
            How to work with Dr. Refai at shrinkMD.
          </h1>
          <p className="mt-6 care-summary text-lg md:text-xl text-secondary font-light leading-relaxed">
            shariqrefai.com is Dr. Refai's educational and media platform. It's
            not a clinical practice and it doesn't accept patients. If you're
            here because you want to see him or one of his colleagues as a
            patient, the clinical practice is{" "}
            <a
              href={withNetworkUtm("https://shrinkmd.com/", "work_with_dr_refai_intro")}
              target="_blank"
              rel="noopener noreferrer"
              className="text-accent underline decoration-accent/40 underline-offset-4 hover:decoration-accent"
            >
              shrinkMD
            </a>
            . This page explains how that works and how to start.
          </p>
        </div>
      </section>

      <article className="py-16 md:py-20 px-4 md:px-8 bg-background">
        <div className="container mx-auto max-w-3xl prose prose-lg max-w-none prose-headings:font-serif prose-headings:font-normal prose-headings:text-primary prose-h2:text-2xl md:prose-h2:text-3xl prose-h2:mt-12 prose-h2:mb-4 prose-p:font-light prose-p:text-secondary prose-p:leading-relaxed prose-li:font-light prose-li:text-secondary prose-strong:text-primary prose-strong:font-medium prose-a:text-accent hover:prose-a:text-primary">
          <p>
            <strong>Financial disclosure.</strong> Dr. Refai founded and holds
            a material ownership interest in shrinkMD Health Inc., the entity
            that operates shrinkMD. Content on shariqrefai.com is educational,
            but linking readers to shrinkMD is a route to a clinical practice
            he owns, and that relationship is disclosed here plainly. See{" "}
            <Link href="/disclosures">Relationships &amp; Financial Disclosure</Link>{" "}
            for the full picture.
          </p>

          <h2>What shrinkMD is</h2>
          <p>
            shrinkMD is an independent telepsychiatry practice. Its
            clinicians provide psychiatric evaluation, diagnosis, medication
            management, and ongoing outpatient care by video visit. It's not a
            wellness app, not a coaching service, and not a life-coaching
            platform. It's a licensed clinical practice.
          </p>

          <h2>States shrinkMD currently serves</h2>
          <p>
            shrinkMD is open to patients in {joinList(STATES_OPEN)}. It's
            coming soon to {joinList(STATES_COMING_SOON)}, and you can join
            the waitlist for those states. State coverage changes as the
            practice grows, so the current list and pricing live on{" "}
            <a
              href={withNetworkUtm("https://shrinkmd.com/", "work_with_dr_refai_states")}
              target="_blank"
              rel="noopener noreferrer"
            >
              shrinkmd.com
            </a>
            . shrinkMD is private pay and doesn't bill insurance.
          </p>

          <h2>What shrinkMD covers</h2>
          <ul>
            <li>Psychiatric evaluation and diagnosis.</li>
            <li>Medication management and ongoing prescribing.</li>
            <li>Follow-up visits and treatment adjustments over time.</li>
            <li>
              Care for adult psychiatric conditions including anxiety
              disorders, panic, OCD, depression, bipolar spectrum conditions,
              insomnia, and women's mental health concerns such as PMDD.
            </li>
            <li>Non-controlled medications only. shrinkMD doesn't prescribe controlled substances.</li>
          </ul>

          <h2>Who's a good fit</h2>
          <ul>
            <li>Adults seeking psychiatric care in a state where shrinkMD is open.</li>
            <li>
              People who want continuity with a licensed psychiatrist rather
              than rotating clinicians.
            </li>
            <li>
              People whose situation is appropriate for outpatient
              telepsychiatry (video visits, remote medication management,
              ongoing follow-up).
            </li>
          </ul>

          <h2>Who's not a good fit</h2>
          <ul>
            <li>
              <strong>Anyone in crisis.</strong> If you're thinking about
              suicide or in immediate danger, call or text 988 in the United
              States, call 911, or go to your nearest emergency room. shrinkMD
              is outpatient care, not a crisis service.
            </li>
            <li>
              <strong>Children and adolescents.</strong> shrinkMD's clinicians
              focus on adult care. Kids and teens need a child and adolescent
              psychiatrist.
            </li>
            <li>
              <strong>Anyone who needs in-person care first.</strong> Some
              conditions and some medication regimens are best started
              in-person. If a shrinkMD clinician thinks that describes you,
              they'll say so and help you find the right setting.
            </li>
            <li>
              <strong>Anyone outside the open states.</strong> shrinkMD's
              clinicians can only see patients where they're licensed and the
              practice is open. The waitlist covers the coming-soon states.
            </li>
          </ul>

          <h2>How to start</h2>
          <p>
            The clinical intake and appointment scheduling all happen at
            shrinkmd.com. You don't need to contact
            shariqrefai.com to become a patient. Go directly to{" "}
            <a
              href={withNetworkUtm("https://shrinkmd.com/start-care", "work_with_dr_refai_start")}
              target="_blank"
              rel="noopener noreferrer"
            >
              shrinkmd.com/start-care
            </a>{" "}
            and start the intake there.
          </p>

          <h2>If shrinkMD isn't right for you</h2>
          <p>
            The goal is that you get appropriate care, not that you get care
            from shrinkMD specifically. Reasonable places to find another
            psychiatrist:
          </p>
          <ul>
            <li>
              Your insurance carrier's in-network psychiatrist directory
              (usually the most current source on who's actually taking
              patients).
            </li>
            <li>
              <a
                href="https://www.psychologytoday.com/us/psychiatrists"
                target="_blank"
                rel="noopener noreferrer"
              >
                Psychology Today's psychiatrist directory
              </a>
              , which is filterable by insurance, state, specialty, and video
              availability.
            </li>
            <li>
              The{" "}
              <a
                href="https://findtreatment.gov/"
                target="_blank"
                rel="noopener noreferrer"
              >
                SAMHSA behavioral health treatment locator (findtreatment.gov)
              </a>
              , the federal directory for mental health and substance use
              care, including sliding-scale and public options.
            </li>
            <li>
              Your primary care physician, who can refer to a psychiatrist
              locally and coordinate care.
            </li>
          </ul>

          <h2>What this page is and isn't</h2>
          <p>
            This page is informational. It explains how to become a patient at
            shrinkMD if you've decided you want to. It's not medical advice,
            it's not a recommendation that you specifically need psychiatric
            care, and it's not an endorsement that shrinkMD is objectively the
            best psychiatric practice. It's transparent plumbing between an
            educational site and the founder's clinical practice.
          </p>

          <h2>Frequently asked questions</h2>
          <div className="faq not-prose space-y-6">
            {FAQ_ITEMS.map((item) => (
              <div key={item.question} className="border-l-2 border-accent pl-5">
                <h3 className="font-serif text-lg text-primary leading-snug">
                  {item.question}
                </h3>
                <p className="mt-2 text-secondary font-light leading-relaxed">
                  {item.answer}
                </p>
              </div>
            ))}
          </div>

          <p className="text-sm mt-12">
            <em>
              shariqrefai.com is an educational author and media platform. It
              does not provide clinical care, treatment, diagnosis, or a
              physician-patient relationship. Reading this page does not
              establish a physician-patient relationship with Dr. Refai or
              shrinkMD. Clinical care is provided at shrinkmd.com under its
              own terms and clinical protocols.
            </em>
          </p>
        </div>
      </article>

      <RelatedPages
        entries={[
          {
            href: "/about",
            label: "About Shariq Refai, MD, MBA",
            why: "Credentials, training, board certifications, and the full clinical background.",
          },
          {
            href: "/medical-reviewer",
            label: "Medical reviewer profile",
            why: "Dr. Refai's editorial role across the Shrink Network.",
          },
          {
            href: "/disclosures",
            label: "Relationships & financial disclosure",
            why: "Full accounting of ownership interests and the relationships between shariqrefai.com and shrinkMD.",
          },
          {
            href: "/faq",
            label: "Frequently asked questions",
            why: "Other common questions about the site, the network, and Dr. Refai's practice.",
          },
        ]}
      />
    </SiteLayout>
  );
}
